import PlusIcon from "@/components/icons/exercise/PlusIcon";
import MatchingOptionIcon from "@/components/icons/exerciseV2/MatchingOptionIcon";
import { Button } from "@/components/ui/Button";
import { useModalAction } from "@/contexts/modal/Context.tsx";
import { ExerciseMatchingOptionsItem, Submission } from "@/types";
import { calculateMaxWidth } from "@/utils/exercises";
import { FC, SetStateAction, useCallback, useEffect, useState } from "react";
import { useDrag, useDrop } from "react-dnd";

export interface WordOption {
  word: string;
  id: number;
  text: string;
  isCorrect?: boolean;
  correctAnswer: string;
}

interface DraggableWordOptionProps {
  id: number;
  word: string;
  onDragStart: (id: number) => void;
  isCorrected: boolean;
  maxWidth: number;
}

interface DroppedWordState {
  id: number;
  word: string;
  isCorrect?: boolean;
}

interface DroppedWord {
  id: number;
  word: string;
  isCorrect?: boolean;
}

interface MovedItem {
  [key: number]: boolean;
}

interface SentenceBuilderProps {
  wordOptions: WordOption[];
  onAllItemsMoved: (allMoved: boolean) => void;
  isCorrected: boolean;
  onCorrected: (correctedOptions: WordOption[]) => void;
  onCorrectAnswersCount: (count: number) => void;
  onDroppedWordsChange: (droppedWords: DroppedWordState[]) => void;
}

const ItemType = {
  WORD_OPTION: "wordOption",
};

interface MatchingOptionGapFillProps {
  data: ExerciseMatchingOptionsItem[];
}

const DraggableWordOption: FC<DraggableWordOptionProps> = ({
  id,
  word,
  onDragStart,
  isCorrected,
  maxWidth,
}) => {
  const [{ isDragging }, drag] = useDrag(() => ({
    type: ItemType.WORD_OPTION,
    item: () => {
      if (isCorrected) {
        return null;
      }
      if (typeof onDragStart === "function") {
        onDragStart(id);
      }
      return { id, word };
    },
    collect: (monitor) => ({
      isDragging: !!monitor.isDragging(),
    }),
  }));

  return (
    <div
      ref={drag}
      className="flex justify-center items-center w-[121px] h-[22px] font-normal text-white whitespace-nowrap bg-brand-secondary rounded-md cursor-grab"
      style={{
        opacity: isDragging ? 0.5 : 1,
        width: `${maxWidth}px`,
        height: "22px",
        minWidth: `121px`,
      }}
    >
      {word}
    </div>
  );
};

const DroppableArea: FC<{
  id: number;
  onDrop: (item: DroppedWordState, id: number) => void;
  droppedWord?: DroppedWordState;
  onRemove: (id: number) => void;
  isCorrected: boolean;
  maxWidth: number;
}> = ({ id, onDrop, droppedWord, onRemove, isCorrected, maxWidth }) => {
  const [, drop] = useDrop(() => ({
    accept: ItemType.WORD_OPTION,
    drop: (item: DroppedWordState) => {
      if (isCorrected) {
        return;
      }
      onDrop(item, id);
    },
  }));

  let bgColor = "border-brand-secondary border-dashed";
  if (isCorrected) {
    bgColor = droppedWord?.isCorrect
      ? "bg-[#219653] text-white border-none"
      : "bg-[#C80505] text-white border-none";
  }

  return (
    <div
      ref={drop}
      style={{
        width: `${maxWidth}px`,
        height: "22px",
        minWidth: `121px`,
      }}
      className={`flex justify-center items-center w-[121px] h-[22px] font-normal whitespace-nowrap rounded-md border ${
        droppedWord
          ? `text-brand border-brand`
          : `text-xs text-brand-secondary border-brand-secondary border-dashed`
      } ${bgColor}`}
    >
      {droppedWord ? (
        <div
          role="button"
          tabIndex={0}
          onClick={() => onRemove(id)}
          onKeyPress={(e) => {
            if (e.key === "Enter" || e.key === " ") onRemove(id);
          }}
          className="interactive-element"
          style={{ cursor: "pointer" }}
        >
          {droppedWord.word}
        </div>
      ) : (
        <div className="flex justify-center items-center gap-1.5">
          <PlusIcon />
          <div className="grow">Drag here</div>
        </div>
      )}
    </div>
  );
};

const SentenceBuilder: FC<SentenceBuilderProps> = ({
  wordOptions,
  onAllItemsMoved,
  isCorrected,
  onCorrected,
  onCorrectAnswersCount,
  onDroppedWordsChange,
}) => {
  const [droppedWords, setDroppedWords] = useState<{
    [key: number]: DroppedWord;
  }>({});
  const [movedItems, setMovedItems] = useState<MovedItem>({});
  const allMoved = Object.keys(movedItems).length === wordOptions.length;
  const [maxWidth, setMaxWidth] = useState<number>(121);

  const handleDrop = (item: DroppedWord, droppableAreaId: number): void => {
    setDroppedWords((prevDroppedWords) => {
      const isAreaOccupied = Object.keys(prevDroppedWords).includes(
        droppableAreaId.toString(),
      );

      if (isAreaOccupied) {
        return prevDroppedWords;
      }

      const newState = {
        ...prevDroppedWords,
        [droppableAreaId]: { id: item.id, word: item.word },
      };
      setMovedItems((prevMovedItems) => ({
        ...prevMovedItems,
        [item.id]: true,
      }));

      return newState;
    });
  };

  const handleRemoveWord = (droppableAreaId: number) => {
    if (isCorrected) {
      return null;
    }
    setDroppedWords((prev) => {
      const newState = { ...prev };
      delete newState[droppableAreaId];
      return newState;
    });

    const itemIdToRemove = droppedWords[droppableAreaId]?.id;
    if (itemIdToRemove != null) {
      setMovedItems((prev) => {
        const newState = { ...prev };
        delete newState[itemIdToRemove];
        return newState;
      });
    }
  };

  const handleDragStart = (id: number) => {
    console.warn(`Inizio drag dell'item con ID: ${id}`);
  };

  useEffect(() => {
    onAllItemsMoved(allMoved);
  }, [allMoved, onAllItemsMoved]);

  const handleCorrection = useCallback(() => {
    let correctCount = 0;
    const correctedOptions = wordOptions.map((option) => {
      const userAnswer = droppedWords[option.id];
      const isCorrect = userAnswer && userAnswer.word === option.correctAnswer;
      if (isCorrect) correctCount++;
      const word = userAnswer ? userAnswer.word : option.word;
      const final = { ...option, word, isCorrect };
      return final;
    });
    onCorrectAnswersCount(correctCount);

    const updatedDroppedWords = { ...droppedWords };
    correctedOptions.forEach((option) => {
      if (updatedDroppedWords[option.id]) {
        updatedDroppedWords[option.id] = {
          ...updatedDroppedWords[option.id],
          isCorrect: option.isCorrect,
        };
      }
    });

    const droppedWordsArray = Object.values(updatedDroppedWords);

    setDroppedWords(updatedDroppedWords);
    onDroppedWordsChange(droppedWordsArray);
    onCorrected(correctedOptions);
  }, [
    droppedWords,
    wordOptions,
    onCorrectAnswersCount,
    onCorrected,
    onDroppedWordsChange,
  ]);

  useEffect(() => {
    if (isCorrected) {
      handleCorrection();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCorrected]);

  useEffect(() => {
    const width = calculateMaxWidth(wordOptions);
    setMaxWidth(width);
  }, [wordOptions]);

  return (
    <div className="flex flex-col text-sm leading-4">
      {wordOptions.map((option, index) => (
        <div key={index} className="flex gap-2.5 mt-1.5 w-full">
          {movedItems[option.id] ? (
            <div
              className="flex justify-center items-center font-normal whitespace-nowrap rounded-md border border-dashed border-brand-secondary"
              style={{
                width: `${maxWidth}px`,
                height: "22px",
                minWidth: `121px`,
              }}
            ></div>
          ) : (
            <DraggableWordOption
              id={option.id}
              word={option.word}
              onDragStart={() => handleDragStart(option.id)}
              isCorrected={isCorrected}
              maxWidth={maxWidth}
            />
          )}
          <DroppableArea
            id={option.id}
            onDrop={handleDrop}
            droppedWord={droppedWords[option.id]}
            onRemove={handleRemoveWord}
            isCorrected={isCorrected}
            maxWidth={maxWidth}
          />
          <div className="grow flex items-center pl-5 font-normal bg-brand-auth rounded-md text-brand-default">
            {option.text}
          </div>
        </div>
      ))}
    </div>
  );
};

function shuffleArray(array: string[]) {
  for (let i = array.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [array[i], array[j]] = [array[j], array[i]];
  }
  return array;
}

const MatchingOptionExercise: FC<MatchingOptionGapFillProps> = ({ data }) => {
  const { openModal } = useModalAction();
  const [wordOptions, setWordOptions] = useState<WordOption[]>([]);
  const [allItemsMoved, setAllItemsMoved] = useState(false);
  const [isCorrected, setIsCorrected] = useState(false);
  const setCorrectedOptions = useState<WordOption[]>([])[1];
  const [correctAnswersCount, setCorrectAnswersCount] = useState(0);
  const [userAnswer, setUserAnswer] = useState<DroppedWordState[]>([]);

  useEffect(() => {
    const options = data.map((item) => ({
      word: item.correct_answer,
      id: item.id,
      text: item.statement,
      correctAnswer: item.correct_answer,
    }));

    const wordsOnly = options.map((option) => option.word);
    const shuffledWords = shuffleArray([...wordsOnly]);

    const shuffledWordOptions = options.map((option, index) => ({
      ...option,
      word: shuffledWords[index],
    }));

    setWordOptions(shuffledWordOptions);
  }, [data]);

  const handleAllItemsMoved = (
    allMoved: boolean | ((prevState: boolean) => boolean),
  ) => {
    setAllItemsMoved(allMoved);
  };

  const startCorrection = () => {
    setIsCorrected(true);
  };

  const handleCorrectAnswersCount = (count: number) => {
    setCorrectAnswersCount(count);
  };

  const handleUserAnswer = (
    newDroppedWords: SetStateAction<DroppedWordState[]>,
  ) => {
    setUserAnswer(newDroppedWords);
  };

  const handleShowMyScore = async () => {
    const submissionData: Submission[] = Object.values(userAnswer).map(
      (answer): Submission => ({
        questionId: answer.id,
        textAnswer: answer.word,
        isCorrect: answer.isCorrect || false,
      }),
    );

    // OPEN MODAL WITH NEXT EXERCISE BUTTON
    openModal(
      "EXERCISE_V2_MODAL",
      {
        exerciseType: "matching_option",
        score: correctAnswersCount,
        total: wordOptions.length,
        contentClass: "bg-brand-lavender",
        submissionData: submissionData,
      },
      {
        renderHeader: (
          <div
            className={
              "flex items-center px-2 h-10 bg-brand-secondary rounded-t-md"
            }
          >
            <p className={"text-14px text-white font-semibold"}>
              Matching option
            </p>
          </div>
        ),
      },
    );
  };

  useEffect(() => {
    // Blocca lo scroll
    document.body.style.overflowY = "hidden";

    // Riabilita lo scroll quando il componente viene smontato
    return () => {
      document.body.style.overflowY = "auto";
    };
  }, []);

  return (
    <div className="flex flex-col gap-3 px-5 py-5 bg-white rounded-lg border border-brand-secondary border-solid">
      <header className="flex gap-5 justify-between w-full">
        <div className="flex gap-5 justify-between text-xl leading-6 text-brand">
          <MatchingOptionIcon className="w-[42px] h-[32px]" />

          <div className="w-full italic">
            <p className="text-sm font-semibold ">Matching option</p>

            <p className="text-xs font-normal italic text-brand">
              Match the answers correctly
            </p>
          </div>
        </div>

        <Button
          className="rounded-[10px] h-[30px] text-[14px]"
          size={"sm"}
          variant={"secondary"}
          disabled={!allItemsMoved || isCorrected}
          onClick={startCorrection}
        >
          Correct now
        </Button>
      </header>
      <div>
        <p className="text-xs font-normal  text-black">
          {data?.[0]?.title ? data?.[0]?.title : ""}
        </p>
      </div>
      <div>
        <SentenceBuilder
          wordOptions={wordOptions}
          onAllItemsMoved={handleAllItemsMoved}
          isCorrected={isCorrected}
          onCorrected={setCorrectedOptions}
          onCorrectAnswersCount={handleCorrectAnswersCount}
          onDroppedWordsChange={handleUserAnswer}
        />
      </div>

      {isCorrected && (
        <Button
          size={"default"}
          variant={"default"}
          className="mx-auto border-brand text-[16px]"
          onClick={() => void handleShowMyScore()}
        >
          My score
        </Button>
      )}
    </div>
  );
};

export default MatchingOptionExercise;
